import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    ArrowLeft, Fingerprint, ShieldCheck, KeyRound, Users, Activity,
    Loader2,
} from "lucide-react";

/**
 * Admin → Security → Authentication.
 * Aggregated privacy-safe metrics — passkey adoption, MFA adoption,
 * passkey success rate. Never exposes individual credentials or PII.
 */
export default function AuthenticationMetrics() {
    const [m, setM] = useState(null);

    const load = async () => {
        try {
            const r = await api.get("/webauthn/metrics");
            setM(r.data);
        } catch { toast.error("Failed to load metrics"); }
    };
    useEffect(() => { load(); }, []);

    if (!m) return <div className="p-8"><Loader2 size={16} className="animate-spin" /></div>;

    const cards = [
        { k: "Passkey adoption", v: `${m.users.passkey_adoption_pct}%`,
          sub: `${m.users.with_passkey} of ${m.users.total} users`, Icon: Fingerprint, tone: "blue" },
        { k: "MFA (TOTP) adoption", v: `${m.users.totp_adoption_pct}%`,
          sub: `${m.users.with_totp} users`, Icon: ShieldCheck, tone: "emerald" },
        { k: "Passkey success rate (30d)", v: `${m.last_30d.passkey_success_pct}%`,
          sub: `${m.last_30d.passkey_success} ok · ${m.last_30d.passkey_failed} failed`,
          Icon: Activity, tone: "indigo" },
        { k: "Passkeys per user", v: m.passkeys.avg_per_user,
          sub: `${m.passkeys.total_credentials} total credentials`, Icon: KeyRound, tone: "amber" },
    ];

    const toneCls = {
        blue:    "bg-blue-50 text-blue-700 border-blue-200",
        emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
        indigo:  "bg-indigo-50 text-indigo-700 border-indigo-200",
        amber:   "bg-amber-50 text-amber-700 border-amber-200",
    };

    return (
        <div className="max-w-5xl mx-auto p-6 md:p-8" data-testid="auth-metrics">
            <div className="mb-4 flex items-center gap-2 text-xs">
                <Link to="/super-admin" className="text-stone-500 hover:text-stone-900 flex items-center gap-1">
                    <ArrowLeft size={12} /> Super Admin
                </Link>
            </div>
            <div className="flex items-start gap-3 mb-6">
                <div className="mt-1 rounded-full bg-emerald-50 p-1.5">
                    <ShieldCheck size={16} className="text-emerald-600" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Authentication Metrics</h1>
                    <p className="text-sm text-stone-500 mt-1 max-w-2xl">
                        Aggregated, privacy-safe adoption of passkeys and MFA across the
                        workspace. No biometric data or individual credential information
                        is exposed here.
                    </p>
                </div>
            </div>

            <div className="grid md:grid-cols-4 gap-3 mb-8" data-testid="metric-cards">
                {cards.map((c) => (
                    <div key={c.k} className={`rounded-xl border p-4 ${toneCls[c.tone]}`}>
                        <c.Icon size={14} className="mb-2 opacity-70" />
                        <div className="text-[10px] uppercase tracking-wide opacity-80">{c.k}</div>
                        <div className="text-2xl font-bold mt-1">{c.v}</div>
                        <div className="text-[11px] opacity-70 mt-1">{c.sub}</div>
                    </div>
                ))}
            </div>

            <section className="rounded-xl border border-stone-200 bg-white p-4">
                <div className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <Users size={14} /> Sign-in activity (last 30 days)
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <MiniStat label="Password logins" value={m.last_30d.password_logins} />
                    <MiniStat label="Passkey success" value={m.last_30d.passkey_success} />
                    <MiniStat label="Passkey failed" value={m.last_30d.passkey_failed} />
                    <MiniStat label="Total users" value={m.users.total} />
                </div>
            </section>

            <div className="mt-4 text-[11px] text-stone-500">
                Generated: {new Date(m.generated_at).toLocaleString()} · {m.note}
            </div>
        </div>
    );
}

function MiniStat({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wide text-stone-500">{label}</div>
            <div className="text-lg font-semibold">{value}</div>
        </div>
    );
}
